import { useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { submitEnquiry } from "./submitEnquiry";

export default function BrochurePopup({ onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", comments: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  function validate() {
    const e = {};
    if (!form.phone || !/^\d{10}$/.test(form.phone))
      e.phone = "Enter a valid 10-digit phone number";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError("");
    try {
      await submitEnquiry({ name: form.name, email: form.email, mobile: form.phone, notes: form.comments });
      onClose();
    } catch {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function field(key) {
    return {
      value: form[key],
      onChange: (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        if (errors[key]) setErrors((err) => ({ ...err, [key]: undefined }));
      },
    };
  }

  const inputBase = `
    w-full h-[48px] sm:h-[56px]
    rounded-[12px] sm:rounded-[14px]
    border bg-transparent px-4
    text-white text-[15px] sm:text-[16px]
    placeholder:text-white/60 outline-none
  `;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm px-3 sm:px-4">
      {/* Modal */}
      <div
        className="
          relative
          w-full
          max-w-[360px]
          sm:max-w-[420px]
          md:max-w-[520px]
          bg-[#14382D]
          rounded-[22px]
          sm:rounded-[28px]
          px-5
          py-5
          sm:px-[30px]
          sm:py-[28px]
          shadow-[0_20px_60px_rgba(0,0,0,0.35)]
        "
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="
            absolute top-3 right-3 sm:top-4 sm:right-4
            w-9 h-9 sm:w-10 sm:h-10
            rounded-full bg-[#D9DCB1]
            flex items-center justify-center
            hover:opacity-90 transition
          "
        >
          <X size={20} className="text-[#14382D]" strokeWidth={2.5} />
        </button>

        {/* Heading */}
        <h2
          className="
            text-[#D9DCB1] text-[20px] sm:text-[24px]
            font-bold uppercase leading-none mb-3
          "
        >
          ENQUIRE NOW
        </h2>

        <p className="text-white text-[14px] sm:text-[16px] leading-[1.45] mb-5 sm:mb-6 max-w-[380px]">
          Please enter the details below to get in touch with us!
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Name */}
          <input
            type="text"
            placeholder="Name"
            className={`${inputBase} border-[#D9DCB1]`}
            {...field("name")}
          />

          {/* Phone */}
          <div>
            <div
              className={`
                h-[48px] sm:h-[56px]
                rounded-[12px] sm:rounded-[14px]
                border flex items-center overflow-hidden
                ${errors.phone ? "border-red-400" : "border-[#D9DCB1]"}
              `}
            >
              <div className="flex items-center gap-1 px-4">
                <span className="text-[22px] sm:text-[28px]">🇮🇳</span>
                <ChevronDown size={14} className="text-[#D9DCB1]" />
              </div>
              <input
                type="tel"
                placeholder="Phone"
                maxLength={10}
                className="flex-1 bg-transparent outline-none text-white text-[15px] sm:text-[16px] placeholder:text-white/60 pr-4"
                {...field("phone")}
              />
            </div>
            {errors.phone && (
              <p className="text-red-400 text-[11px] mt-1 pl-1">{errors.phone}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              placeholder="Email"
              className={`${inputBase} ${errors.email ? "border-red-400" : "border-[#D9DCB1]"}`}
              {...field("email")}
            />
            {errors.email && (
              <p className="text-red-400 text-[11px] mt-1 pl-1">{errors.email}</p>
            )}
          </div>

          {/* Comments */}
          <input
            type="text"
            placeholder="Comments"
            className={`${inputBase} border-[#D9DCB1]`}
            {...field("comments")}
          />

          {apiError && (
            <p className="text-red-400 text-[12px] text-center">{apiError}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="
              w-full h-[54px] sm:h-[62px]
              rounded-[12px] sm:rounded-[14px]
              bg-[#D9DCB1] text-[#14382D]
              text-[18px] sm:text-[22px]
              font-bold uppercase
              mt-1 sm:mt-2
              hover:opacity-95 transition
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          >
            {submitting ? "Submitting..." : "ENQUIRE NOW"}
          </button>
        </form>
      </div>
    </div>
  );
}
